import { createContext, useContext, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import apiClient from "../../api-client.js";
import { getData } from "../hooks/getData.js";

/**
 * @typedef {'superadmin'|'agency_admin'|'consultant'|'client'|'freelance_ticket'} UserRole
 */

/**
 * @typedef {Object} User
 * @property {string} id - User unique identifier
 * @property {string} name - User display name
 * @property {string} email - User email address
 * @property {UserRole} role - User role
 * @property {string} [agencyId] - Optional agency ID for associated users
 * @property {number|Array<{id: number, name: string}>} [client] - Optional client ID or list for users with role "client"
 * @property {string} [profile_image] - Optional profile image URL
 * @property {string} [first_name] - User first name
 * @property {string} [last_name] - User last name
 * @property {string} [telephone] - User telephone
 * @property {Array<{id: number, name: string}>} [invited_agencies] - Optional invited agencies for consultants
 * @property {boolean|Object} [active_setup] - Optional setup status (boolean or per-client map)
 * @property {boolean|Object} [setup_not_needed] - Optional flag marking the setup as "no necesario" (boolean or per-client map)
 * @property {boolean} [has_content_config] - Optional flag indicating if client has content generator config
 * @property {boolean} [has_projects] - Optional flag indicating if client has custom projects
 * @property {Object|null} [integrations] - Linked metric integrations {ga4, search_console, seranking} (or per-client map) for client users
 * @property {boolean} [clientify_id] - Optional flag from /me used to gate Leads for agency_admin/consultant
 * @property {Array<number>|Array<{announcement_id?: number, announcementId?: number, id?: number, notification_id?: number, notificationId?: number}>} [unopened_announcements] - Unopened central announcements for agency users when provided by backend
 */

/**
 * @typedef {Object} AuthContextType
 * @property {User|null} user - Currently authenticated user
 * @property {boolean} isAuthenticated - Whether user is authenticated
 * @property {boolean} isLoading - Whether auth is loading
 * @property {boolean} interceptorReady - Whether API interceptors are ready
 * @property {Object} permissions - User permissions based on role
 * @property {function(string, string): Promise<{success: boolean, error?: string, message?: string}>} login - Login function
 * @property {function(): void} logout - Logout function
 * @property {function(): Promise<boolean>} refreshUser - Refresh user data from API
 * @property {function(Object): void} applyProfile - Store a /users/me/ profile as the current user
 */

const AuthContext = createContext(undefined);

const AUTH_STORAGE_KEY = "markezy_auth";
const ACCESS_TOKEN_KEY = "markezy_access_token";
const REFRESH_TOKEN_KEY = "markezy_refresh_token";
const ACCESS_DISABLED_CODE = "access_disabled";

const buildUserData = (profile) => ({
  id: profile.id,
  name: `${profile.first_name} ${profile.last_name}`,
  email: profile.email,
  role: profile.role,
  agencyId: profile.agency,
  agencyName: profile.agency_name,
  agencyType: profile.agency_type ?? null,
  agencyFeatures: profile.agency_features || {},
  agencyBranding: profile.agency_branding || null,
  agencyPlan: profile.agency_plan || null,
  profile_image: profile.profile_image,
  first_name: profile.first_name,
  last_name: profile.last_name,
  telephone: profile.telephone,
  invited_agencies: profile.invited_agencies || [],
  clientify_id: !!profile.clientify_id,
  clientify_user_id: profile.clientify_user_id ?? null,
  unopened_announcements: profile.unopened_announcements || [],
  ui_preferences: profile.ui_preferences || {},
  profile_pending: profile.profile_pending,
  ...(profile.client && { client: profile.client }),
  ...(profile.active_setup !== undefined && { active_setup: profile.active_setup }),
  ...(profile.setup_not_needed !== undefined && { setup_not_needed: profile.setup_not_needed }),
  ...(profile.has_content_config !== undefined && { has_content_config: profile.has_content_config }),
  ...(profile.has_projects !== undefined && { has_projects: profile.has_projects }),
  ...(profile.integrations !== undefined && { integrations: profile.integrations }),
});

// Default permissions for each role
const defaultPermissions = {
  dashboard: true,
  clients: true,
  team: true,
  projects: true,
  conversations: true,
  incidents: true,
  'technical-setup': true,
  'marketing-plan': true,
  onboarding: true,
};

const getPermissionsForRole = (role) => {
  let allowed = [];

  switch (role) {
    case "superadmin":
      // Super agency_admin has access to everything
      allowed = Object.keys(defaultPermissions);
      break;
    case "agency_admin":
      // Agency agency_admin has access to most features
      allowed = [
        "dashboard",
        "clients",
        "team",
        "projects",
        "conversations",
        "incidents",
        "setup",
        "marketing-plan",
        "onboarding",
        "user"
      ];
      break;
    case "consultant":
      // Consultant has limited access
      allowed = [
        "dashboard",
        "clients",
        "projects",
        "conversations",
        "incidents",
        "setup",
        "marketing-plan",
        "user"
      ];
      break;
    case "client":
      // Client has very limited access
      allowed = [
        "dashboard",
        "projects",
        "conversations",
        "onboarding",
        "setup",
        "user"
      ];
      break;
    case "freelance_ticket":
      allowed = [
        "dashboard",
        "incidents",
        "user"
      ];
      break;
    default:
      return Object.fromEntries(
        Object.keys(defaultPermissions).map((k) => [k, false])
      );
  }

  return Object.fromEntries(
    Object.keys(defaultPermissions).map((key) => [key, allowed.includes(key)])
  );
};

// Global interceptor installation
let interceptorsInstalled = false;

function installApiClientInterceptors(logout) {
  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    if (error) {
      logout();
    }
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue = [];
  };

  // Request interceptor to add auth token
  apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  });

  // Response interceptor to handle 401 errors and token refresh
  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && error.response.data?.code === ACCESS_DISABLED_CODE) {
        logout(error.response.data.detail);
        return Promise.reject(error);
      }

      if (
        error.response &&
        error.response.status === 401 &&
        !originalRequest._retry
      ) {
        // Skip refresh logic for login endpoint
        if (originalRequest.url && originalRequest.url.includes("users/auth/login/")) {
          return Promise.reject(error);
        }

        // If refresh endpoint returns 401, logout
        if (originalRequest.url && originalRequest.url.includes("users/auth/refresh/")) {
          logout();
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              return apiClient(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken) {
          logout();
          return Promise.reject(error);
        }

        try {
          const response = await apiClient.post("users/auth/refresh/", {
            refresh: refreshToken,
          });

          if (response && response.data) {
            localStorage.setItem(ACCESS_TOKEN_KEY, response.data.access);
            localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refresh);

            processQueue(null, response.data.access);

            originalRequest.headers["Authorization"] = `Bearer ${response.data.access}`;
            return apiClient(originalRequest);
          } else {
            processQueue(error, null);
            logout();
            return Promise.reject(error);
          }
        } catch (refreshError) {
          logout();
          processQueue(refreshError, null);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
}

/**
 * Authentication context provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element}
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [interceptorReady, setInterceptorReady] = useState(false);
  const [permissions, setPermissions] = useState(defaultPermissions);
  const [accessDisabledMessage, setAccessDisabledMessage] = useState("");

  const logoutRef = useRef();

  // Load user from localStorage on mount (for immediate UI)
  useEffect(() => {
    try {
      const storedAuth = localStorage.getItem(AUTH_STORAGE_KEY);
      const storedToken = localStorage.getItem(ACCESS_TOKEN_KEY);

      if (storedAuth && storedToken) {
        const authData = JSON.parse(storedAuth);
        if (authData.user && authData.timestamp) {
          setUser(authData.user);
          setPermissions(getPermissionsForRole(authData.user.role));
        }
      }
    } catch (error) {
      console.error("Error loading authentication data:", error);
      localStorage.removeItem(AUTH_STORAGE_KEY);
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem("selectedFilterData");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Install interceptors once
  useEffect(() => {
    const logout = (accessDisabledDetail) => {
      if (accessDisabledDetail) setAccessDisabledMessage(accessDisabledDetail);
      setUser(null);
      setPermissions(getPermissionsForRole(""));
      localStorage.removeItem(AUTH_STORAGE_KEY);
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    };

    logoutRef.current = logout;
    installApiClientInterceptors((accessDisabledDetail) => logoutRef.current(accessDisabledDetail));
    setInterceptorReady(true);
  }, []);

  // Update permissions when user changes
  useEffect(() => {
    if (user && user.role) {
      setPermissions(getPermissionsForRole(user.role));
    }
  }, [user]);

  // Polling cada 10 minutos para llamar a /me usando getData (solo para clientes)
  const [meRefreshKey, setMeRefreshKey] = useState(0);

  useEffect(() => {
    if (interceptorReady && user && user.role === "client") {
      const interval = setInterval(() => {
        setMeRefreshKey((prev) => prev + 1);
      }, 10 * 60 * 1000); // 10 minutos

      return () => clearInterval(interval);
    }
  }, [interceptorReady, user]);

  // Llamar a /me al montar para cualquier usuario autenticado.
  // Para clientes, además se refresca por polling cada 10 minutos.
  const { data: meData } = getData(
    "users/me/",
    interceptorReady && !!user,
    [meRefreshKey, user?.id]
  );

  // Actualizar el usuario cuando lleguen los datos de /me
  useEffect(() => {
    if (meData) {
      const userData = buildUserData(meData);

      const authData = {
        user: userData,
        timestamp: new Date().toISOString(),
      };

      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
      setUser(userData);
      setPermissions(getPermissionsForRole(userData.role));
    }
  }, [meData]);

  /**
   * Login function
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<{success: boolean, error?: string, message?: string}>} Login result
   */
  const login = async (email, password) => {
    try {
      const response = await apiClient.post("users/auth/login/", {
        email: email,
        password: password,
      });

      if (response && response.data) {
        setAccessDisabledMessage("");
        // Store tokens
        localStorage.setItem(ACCESS_TOKEN_KEY, response.data.access);
        localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refresh);

        // Fetch user profile to get complete user data
        try {
          const userResponse = await apiClient.get("users/me/");
          if (userResponse && userResponse.data) {
            const userData = buildUserData(userResponse.data);

            const authData = {
              user: userData,
              timestamp: new Date().toISOString(),
            };

            localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
            setUser(userData);
            setPermissions(getPermissionsForRole(userData.role));

            return { success: true };
          }
        } catch {
          // If user profile fetch fails, use data from login response
          const userData = {
            id: "unknown",
            name: email,
            email: email,
            role: response.data.role,
            agencyId: null,
            invited_agencies: [],
          };

          const authData = {
            user: userData,
            timestamp: new Date().toISOString(),
          };

          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
          setUser(userData);
          setPermissions(getPermissionsForRole(userData.role));

          return { success: true };
        }
      }

      return { success: false, error: "invalid_response" };
    } catch (error) {
      if (error.response && error.response.status === 401) {
        return {
          success: false,
          error: "unauthorized",
          message: error.response.data?.detail,
        };
      }
      return { success: false, error: "network" };
    }
  };

  /**
   * Refresh user data from API
   * @returns {Promise<boolean>} Success status
   */
  // Guarda un perfil de /users/me/ (o de una mutación que lo devuelva) como usuario actual
  const applyProfile = (profile) => {
    const userData = buildUserData(profile);
    localStorage.setItem(
      AUTH_STORAGE_KEY,
      JSON.stringify({ user: userData, timestamp: new Date().toISOString() })
    );
    setUser(userData);
    setPermissions(getPermissionsForRole(userData.role));
  };

  const refreshUser = async () => {
    try {
      const response = await apiClient.get("users/me/");
      if (response && response.data) {
        applyProfile(response.data);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error refreshing user data:", error);
      return false;
    }
  };

  const authenticateWithTokens = async ({ access, refresh }) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, access);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
    return refreshUser();
  };

  /**
   * Logout function. Calls backend to blacklist the refresh token, then clears local state.
   */
  const logout = () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      apiClient
        .post("users/auth/logout/", { refresh: refreshToken })
        .catch(() => { /* ignore errors: we still clear local state */ });
    }
    setUser(null);
    setPermissions(getPermissionsForRole(""));
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem("selectedFilterData");
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    interceptorReady,
    permissions,
    accessDisabledMessage,
    login,
    authenticateWithTokens,
    logout,
    refreshUser,
    applyProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to use the Auth context
 * @returns {AuthContextType} The auth context value
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
